/* eslint-disable */
// /admin/dashboard — mounts the ORIGINAL Larinz Dashboard component (extracted
// from the source bundle) full-screen. We monkey-patch the `persistData()`
// helper used by the dashboard so every edit is PUT to /api/site instead of
// only being saved to localStorage. The marketing site behind the dashboard
// stays in sync via the existing `ll-data-changed` event.

import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/larinz/auth';

import '@/larinz/components.jsx';  // ensure components + window globals are loaded
import '@/admin/dashboard-source.jsx';  // defines window.Dashboard

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { token, user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const [savingState, setSavingState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  // Wire persistData → backend (debounced 500ms; admin keystrokes batch into one PUT).
  useEffect(() => {
    if (!token) return;
    window.LL_AUTH_TOKEN = token;

    let pendingData = null;
    let timer = null;

    const flush = async () => {
      if (!pendingData) return;
      const data = pendingData;
      pendingData = null;
      setSavingState('saving');
      try {
        await window.LL_API.saveSite(data, window.LL_AUTH_TOKEN);
        setSavingState('saved');
        setTimeout(() => setSavingState(s => (s === 'saved' ? 'idle' : s)), 1500);
      } catch (e) {
        console.error('Save failed', e);
        setSavingState('error');
      }
    };

    window.persistData = function persistDataDebounced(data) {
      window.dispatchEvent(new CustomEvent('ll-data-changed', { detail: data }));
      pendingData = data;
      setSavingState('saving');
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, 500);
    };

    // Also flush on visibility change / unload so edits don't get lost.
    const onHide = () => { if (timer) { clearTimeout(timer); flush(); } };
    window.addEventListener('beforeunload', onHide);
    document.addEventListener('visibilitychange', onHide);

    return () => {
      window.removeEventListener('beforeunload', onHide);
      document.removeEventListener('visibilitychange', onHide);
      if (timer) clearTimeout(timer);
      delete window.LL_AUTH_TOKEN;
    };
  }, [token]);

  if (checking) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const Dashboard = window.Dashboard;
  if (!Dashboard) {
    return <div style={{ padding: 80, fontFamily: 'Inter' }}>Dashboard component not loaded.</div>;
  }

  const onLogout = () => { logout(); navigate('/admin/login'); };
  const onClose  = () => navigate('/');

  return (
    <>
      <Dashboard open={true} onClose={onClose} onLogout={onLogout} />
      <SaveBadge state={savingState} />
    </>
  );
}

function SaveBadge({ state }) {
  if (state === 'idle') return null;
  const styles = {
    saving: { bg: '#111', text: '#A5FF12', label: 'Saving…' },
    saved:  { bg: '#A5FF12', text: '#111', label: '✓ Saved' },
    error:  { bg: '#ff3333', text: '#fff', label: '✗ Save failed' },
  };
  const s = styles[state] || styles.idle;
  return (
    <div data-testid="admin-save-badge" style={{
      position: 'fixed', bottom: 18, left: 18, zIndex: 9999,
      padding: '8px 14px', borderRadius: 999,
      background: s.bg, color: s.text, fontFamily: 'Inter',
      fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
      boxShadow: '0 4px 16px rgba(0,0,0,.25)',
    }}>{s.label}</div>
  );
}
